import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { PRODUCT_CLASS_CAMPAIGN, PRODUCT_CLASS_LANGUAGE_LEARNING, PRODUCT_CLASS_LOTE } from '../../store/constants';
import RoutingUtils from '../utils/utils-routing';
import Utils from '../utils';
import { GenreMetaData, ProductMetaData, SubGenreMetaData } from '../../../models/Product';
import { useLanguage } from '../../contexts/language';
import { LocationDescriptor } from 'history';
import { useTranslation } from 'react-i18next';
import { getGenreId, getSubGenreId } from '../common/translationMappers/genreIdMapper';

type Props = {
    readonly product?: ProductMetaData;
    readonly loanFormatKey?: string | null;
    readonly showDetails?: boolean;
    readonly showGenresInSeparateLines?: string | boolean;
    readonly linked?: boolean;
    readonly isLibx?: boolean;
};

export const ProductGenres: FC<Props> = ({
    product = {},
    loanFormatKey,
    showDetails = false,
    showGenresInSeparateLines = false,
    linked = true,
    isLibx = false
}) => {
    const { genres } = product;
    const language = useLanguage();
    const { t } = useTranslation();
    if (!genres) {
        return null;
    }

    const genresItems = flatMap(genres, genre => {
        if (genre.subGenres && showDetails) {
            return genre.subGenres.map(subGenre => {
                const linkTo = setSubGenreLink(product, genre, subGenre, isLibx, loanFormatKey, language);
                const subGenreId = getSubGenreId(subGenre?.id);
                const genreId = getGenreId(genre?.id);

                return linked ? (
                    <Link key={subGenre.id} className="subgenre" to={linkTo ?? '#'}>
                        {`${t(`filters:genre.${genreId}`, genre.name)} > ${t(
                            `filters:genre.${subGenreId}`,
                            subGenre.name
                        )}`}
                    </Link>
                ) : (
                    `${t(`filters:genre.${genreId}`, genre.name)} > ${t(`filters:genre.${subGenreId}`, subGenre.name)}`
                );
            });
        } else {
            if (linked) {
                const linkTo = setGenreLink(product, genre, isLibx, loanFormatKey, language);
                const genreId = getGenreId(genre?.id);
                return (
                    <Link key={genre.id} to={linkTo}>
                        {`${t(`filters:genre.${genreId}`, genre.name)}`}
                    </Link>
                );
            } else {
                const isEncoded = genre.id.includes('@@');
                const genreId = isEncoded ? getGenreId(genre.id) : genre.id;
                return `${t(`filters:genre.${genreId}`, genre.name)}`;
            }
        }
    });

    if (showGenresInSeparateLines) {
        return genresItems.map((g, index) => <p key={`sep-line-genre-${index}`}>{g}</p>);
    }

    if (isEmpty(genresItems)) {
        return null;
    }

    return <p>{Utils.safeJoinComponents(genresItems, ', ')}</p>;
};

export default ProductGenres;

function setGenreLink(
    product: { readonly productClass?: string },
    genre: GenreMetaData,
    isLibx: boolean,
    loanFormatKey: string | null | undefined,
    language: string
): LocationDescriptor {
    const isForeignLanguageProduct = product.productClass === PRODUCT_CLASS_LOTE;
    const isLanguageLearningProduct = product.productClass === PRODUCT_CLASS_LANGUAGE_LEARNING;
    const isCampaign = product.productClass === PRODUCT_CLASS_CAMPAIGN;

    if (isLibx) {
        return RoutingUtils.libxGenrePageLocationDescriptor(genre.id);
    }

    if (isLanguageLearningProduct) {
        const filter = { genre: { include: [genre.id] } };
        return RoutingUtils.languageLearningDescriptorString(language, loanFormatKey, filter);
    }

    if (isForeignLanguageProduct) {
        return RoutingUtils.genrePageForeignLanguageLocationDescriptor(loanFormatKey, language, genre.id);
    }

    if (isCampaign) {
        const filter = { genre: { include: [genre.id] } };
        return RoutingUtils.campaignsLocationDescriptorString(loanFormatKey, filter);
    }

    return RoutingUtils.genrePageLocationDescriptor(loanFormatKey, genre.id);
}

function setSubGenreLink(
    product: { readonly productClass?: string },
    genre: GenreMetaData,
    subGenre: SubGenreMetaData,
    isLibx: boolean,
    loanFormatKey: string | null | undefined,
    language: string
): LocationDescriptor {
    const isForeignLanguageProduct = product.productClass === PRODUCT_CLASS_LOTE;
    const isLanguageLearningProduct = product.productClass === PRODUCT_CLASS_LANGUAGE_LEARNING;

    if (isLibx) {
        return RoutingUtils.libxGenrePageLocationDescriptor(genre.id, subGenre.id);
    }

    if (isLanguageLearningProduct) {
        const filter = { genre: { include: [`${subGenre.id}`] } };

        if (isEqual(genre.name, 'Language Learning')) {
            const targetLanguage = subGenre.name;
            return RoutingUtils.languageLearningGenreDescriptorString(targetLanguage, loanFormatKey);
        } else {
            return RoutingUtils.languageLearningDescriptorString(language, loanFormatKey, filter);
        }
    }

    if (isForeignLanguageProduct) {
        return RoutingUtils.genrePageForeignLanguageLocationDescriptor(loanFormatKey, language, genre.id, subGenre.id);
    }

    return RoutingUtils.genrePageLocationDescriptor(loanFormatKey, genre.id, subGenre.id);
}
